import React, { type FC, type ReactElement, type ReactNode, useCallback, useState } from 'react'
import {
    alpha,
    Tab as BaseTab,
    type TabsProps as BaseTabsProps,
    Box,
    Divider,
    type SxProps,
    tabClasses,
    tabsClasses,
    tabScrollButtonClasses,
    type Theme,
} from '@mui/material'
import { TabContext, TabList, TabPanel } from '@mui/lab'

import { isArrayNotEmpty } from '../../../utils'

import { type Capability, isTranslation, type Translation } from '../../../domain'

import { useTranslator } from '../Internationalization'
import { useHasCapabilities } from '../routing'
import { NoPermission } from './NoPermission'

const tabPanelStyle: SxProps<Theme> = { px: 0 }

const tabSxProps: SxProps<Theme> = (theme) => ({
    ...theme.typography.body2,
    fontWeight: 400,
    color: 'grey.500',
    textTransform: 'initial',
    minHeight: 'inherit',
    minWidth: 'unset',
    px: 2,
    py: 1,
    mr: 1,
    borderBottom: 1.5,
    borderBottomColor: 'transparent',
    '&:hover': {
        color: 'black',
        borderBottomColor: 'grey.400',
    },
    [`&.${tabClasses.selected}`]: {
        color: 'primary.700',
    },
})

const tabListStyle: SxProps<Theme> = (theme) => {
    const bgColor = theme.palette.background.default
    return {
        minHeight: 31,
        position: 'relative',
        '& [role="tablist"]': { height: '100%' },
        '&:hover': {
            [`& .${tabsClasses.scrollButtons}`]: { visibility: 'visible' },
        },
        // fade effect on the left & right edges of tablist
        '&::before, &::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            bottom: 0,
            width: 56,
            zIndex: 1,
            pointerEvents: 'none',
            opacity: 0,
        },
        '&::before': {
            left: 0,
            background: `linear-gradient(to right, ${bgColor}, ${alpha(bgColor, 0)})`,
        },
        '&::after': {
            right: 0,
            background: `linear-gradient(to left, ${bgColor}, ${alpha(bgColor, 0)})`,
        },
        // displays each fade only when the matching scroll button is enabled
        [`&:has(> .${tabsClasses.scrollButtons}:not(.${tabScrollButtonClasses.disabled}):first-of-type)::before`]: {
            opacity: 1,
        },
        [`&:has(> .${tabsClasses.scrollButtons}:not(.${tabScrollButtonClasses.disabled}):last-of-type)::after`]: {
            opacity: 1,
        },
    }
}
const scrollButtonsStyle: SxProps<Theme> = (theme) => ({
    visibility: 'hidden',
    width: 24,
    height: 24,
    position: 'absolute',
    zIndex: 2,
    '&:last-of-type': { right: 0 },
    backgroundColor: 'white',
    m: 0.5,
    color: 'grey.700',
    opacity: 1,
    borderRadius: 1,
    boxShadow: theme.shadows[2],
    [`&.${tabScrollButtonClasses.disabled}`]: { opacity: 0 },
})
const tabIndicatorStyle: SxProps<Theme> = { backgroundColor: 'primary.700', height: 1.5 }
const dividerStyle: SxProps<Theme> = { borderColor: 'grey.300', marginTop: '-1px' }

const labelStyle: SxProps<Theme> = { display: 'flex', alignItems: 'center' }
const counterStyle: SxProps<Theme> = {
    ml: 2,
    px: 1,
    borderRadius: 1,
    backgroundColor: 'grey.200',
    minWidth: 24,
    [`.${tabClasses.root}:hover:not(.${tabClasses.selected}) &`]: { backgroundColor: 'grey.300', color: 'grey.500' },
    [`.${tabClasses.selected} &`]: { backgroundColor: 'primary.100' },
}

const Label: FC<Pick<TabProps<unknown>, 'title' | 'count'>> = ({ title, count }) => {
    return (
        <Box sx={labelStyle}>
            {title}
            {count ? <Box sx={counterStyle}>{count}</Box> : null}
        </Box>
    )
}

type TabProps<TabValue,> = Readonly<{
    /** Used to translate the field */
    title: Translation | string
    /** Displayed in UI and is used to properly translate the field to singular or plural */
    count?: number
    /** Overide the disabled values given by the the capabilities check */
    disabled?: boolean
    children?: ReactNode
    requiredCapabilities?: readonly Capability[]
    value?: TabValue
}>

export const Tab: FC<TabProps<string>> = () => <></>

export type TabsProps<TabValue,> = Readonly<{
    children: ReactElement<TabProps<TabValue>>[]
    onChange?: (value: TabValue) => void
    value?: TabValue
}> &
    Pick<BaseTabsProps, 'orientation'>

export const Tabs = <TabValue extends string,>({ children, onChange, value: controlledValue, ...props }: TabsProps<TabValue>): ReturnType<FC> => {
    const translation = useTranslator()
    const hasCapabilities = useHasCapabilities()

    const enabledValues: string[] = []
    const tabs: ReactNode[] = []
    const panels: ReactNode[] = []

    children.forEach(({ props: { title, value: customValue, count, disabled, requiredCapabilities: capabilities = [], children } }, k) => {
        const value = customValue ?? String(k)
        const actuallyDisabled = disabled || (isArrayNotEmpty(capabilities) && !hasCapabilities(capabilities))

        if (!actuallyDisabled) {
            enabledValues.push(value)
        }

        const translatedTitle = isTranslation(title) ? translation.formatTranslation(title, { count }) : title

        tabs.push(
            <BaseTab
                key={value}
                data-testid={translatedTitle}
                label={<Label title={translatedTitle} count={count} />}
                value={value}
                disabled={actuallyDisabled}
                sx={tabSxProps}
                disableRipple
            />
        )

        if (children) {
            panels.push(
                <TabPanel sx={tabPanelStyle} key={value} value={value}>
                    {children}
                </TabPanel>
            )
        }
    })

    const [selection, setValue] = useState<TabValue | null>(null)

    /** If there is a selection, and its enabled, use it, otherwise, overwrite it */
    const value = controlledValue ?? (selection && enabledValues.includes(selection) ? selection : enabledValues[0])

    const handleOnChange = useCallback((_: unknown, value: TabValue) => {
        if (onChange) {
            onChange(value)
        } else {
            setValue(value)
        }
    }, [])

    return (
        <TabContext value={value || '0'}>
            <TabList
                {...props}
                onChange={handleOnChange}
                sx={tabListStyle}
                slotProps={{
                    indicator: { sx: tabIndicatorStyle },
                    scrollButtons: { sx: scrollButtonsStyle },
                }}
                variant="scrollable"
            >
                {tabs}
            </TabList>
            <Divider sx={dividerStyle} />
            {value ? panels : <NoPermission />}
        </TabContext>
    )
}
